import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Send, Bell, Users, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface NotificationHistory {
  id: string;
  title: string;
  content: string;
  recipients: string[] | 'all';
  sentBy: string;
  sentAt: string;
  category: string;
  actionUrl?: string;
  status: 'sent' | 'failed';
  errorMessage?: string;
}

export function NotificationManager() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [actionUrl, setActionUrl] = useState('');
  const [recipientType, setRecipientType] = useState<'all' | 'selected'>('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [testMode, setTestMode] = useState(false);

  // Fetch users and history on mount
  useEffect(() => {
    fetchUsers();
    fetchHistory();
  }, []);

  const fetchUsers = async () => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/users`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    }
  };

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/notifications?limit=50`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch notification history');
      }

      const data = await response.json();
      setHistory(data.notifications || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load notification history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendNotification = async () => {
    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!content.trim()) {
      setError('Content is required');
      return;
    }
    if (recipientType === 'selected' && selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    setIsSending(true);
    setError('');
    setSuccess('');

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/notifications/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          content,
          recipients: recipientType === 'all' ? 'all' : selectedUsers,
          category,
          actionUrl: actionUrl.trim() || undefined,
          sentBy: user?.uid || 'anonymous',
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send notification');
      }

      const data = await response.json();
      
      const recipientCount = recipientType === 'all' ? 'all users' : `${selectedUsers.length} user(s)`;
      setSuccess(`Notification sent successfully to ${recipientCount}!`);
      
      // Reset form
      setTitle('');
      setContent('');
      setActionUrl('');
      setSelectedUsers([]);
      
      // Refresh history
      fetchHistory();
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setIsSending(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleQuickTest = async () => {
    if (!user?.uid) {
      setError('No user found');
      return;
    }

    setIsSending(true);
    setError('');
    setSuccess('');

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/notifications/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: 'Test Notification',
          content: `This is a test notification sent at ${new Date().toLocaleTimeString()}`,
          recipients: [user.uid],
          category: 'general',
          sentBy: user.uid,
          testMode: true,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send test notification');
      }

      setSuccess('Test notification sent! Check the dropdown and browser notification.');
      fetchHistory();
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err instanceof Error ? err.message : 'Failed to send test notification');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Send Notification Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Notification
          </CardTitle>
          <CardDescription>
            Send push notifications to users or selected individuals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
              {success}
            </div>
          )}

          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., New Game Available!"
              maxLength={100}
            />
            <p className="text-xs text-gray-500 mt-1">{title.length}/100 characters</p>
          </div>

          {/* Content */}
          <div>
            <Label htmlFor="content">Message *</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your notification message..."
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">{content.length}/500 characters</p>
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="game">Game</SelectItem>
                <SelectItem value="announcement">Announcement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action URL (Optional) */}
          <div>
            <Label htmlFor="action-url">Action URL (Optional)</Label>
            <Input
              id="action-url"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="e.g., /lobby or https://example.com"
            />
            <p className="text-xs text-gray-500 mt-1">Users will be redirected here when clicking the notification</p>
          </div>

          {/* Recipients */}
          <div>
            <Label>Recipients *</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={recipientType === 'all'}
                  onChange={() => setRecipientType('all')}
                  className="w-4 h-4"
                />
                <span className="text-sm">All Users</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={recipientType === 'selected'}
                  onChange={() => setRecipientType('selected')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Select Users</span>
              </label>
            </div>
          </div>

          {/* User Selection */}
          {recipientType === 'selected' && (
            <div className="border rounded-md p-4 max-h-60 overflow-y-auto">
              <Label className="mb-2 block">Select Users ({selectedUsers.length} selected)</Label>
              <div className="space-y-2">
                {users.map(u => (
                  <label key={u.uid} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(u.uid)}
                      onChange={() => toggleUserSelection(u.uid)}
                      className="w-4 h-4"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      {u.photoURL && (
                        <img src={u.photoURL} alt="" className="w-6 h-6 rounded-full" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">{u.displayName || 'Anonymous'}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </div>
                    </div>
                  </label>
                ))}
                {users.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No users found</p>
                )}
              </div>
            </div>
          )}

          {/* Send Button */}
          <Button
            onClick={handleSendNotification}
            disabled={isSending}
            className="w-full"
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Notification
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Notification History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Notification History
          </CardTitle>
          <CardDescription>
            Recent notifications sent from this dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No notifications sent yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map(notif => (
                <div key={notif.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-sm">{notif.title}</h4>
                        {notif.status === 'sent' ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Sent
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Failed
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{notif.content}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Badge variant="outline" className="text-xs">
                      {notif.category}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <Users className="w-3 h-3 mr-1" />
                      {notif.recipients === 'all' 
                        ? 'All users' 
                        : `${Array.isArray(notif.recipients) ? notif.recipients.length : 0} user(s)`
                      }
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDate(notif.sentAt)}
                    </Badge>
                  </div>

                  {notif.actionUrl && (
                    <p className="text-xs text-blue-600 mt-2">
                      Action: {notif.actionUrl}
                    </p>
                  )}

                  {notif.errorMessage && (
                    <p className="text-xs text-red-600 mt-2">
                      Error: {notif.errorMessage}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default NotificationManager;

